import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, CircularProgress, TextField, IconButton, Alert, Tooltip,
  Checkbox, Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AiIcon,
  QuestionAnswer as QuestionsIcon,
  Add as AddIcon,
  EditNote as ManualIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';

// Tope de preguntas por cargo (el backend también lo valida).
const MAX_QUESTIONS = 10;

const purpleContained = { backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } };
const purpleOutlined = {
  borderColor: '#7C3AED',
  color: '#7C3AED',
  '&:hover': { borderColor: '#6D28D9', backgroundColor: '#F5F3FF' },
};

const numberBadgeSx = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  backgroundColor: '#6A5AF9',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.72rem',
  fontWeight: 600,
  flexShrink: 0,
};

let tempKeySeq = 0;
const newTempKey = () => `tmp-${Date.now()}-${tempKeySeq++}`;

// Preguntas guardadas del cargo → borrador local (key estable para React)
const toDraft = (questions = []) =>
  questions.map((q) => ({
    key: q.id,
    id: q.id,
    suggestionId: null,
    text: q.text || '',
    rationale: q.rationale || null,
  }));

/**
 * Preguntas de entrevista del CARGO (se abre desde el modal de entrevistas).
 *
 * Es el único lugar donde se definen: el set es uno por vacante, compartido
 * por el equipo e igual para todos los candidatos; las respuestas de cada
 * candidato se anotan en su ficha (drawer).
 *
 * Guardado explícito: editar, agregar, quitar y elegir sugerencias se hace en
 * un borrador local y "Guardar" reemplaza el set completo (máx. 10); después
 * vuelve al modal de entrevistas. "Atrás" (o la X) vuelve sin guardar y avisa
 * si hay cambios. Si otro usuario guardó entremedio, se ofrece cargar su
 * versión o guardar la propia igual.
 *
 * La IA sugiere siempre 5 preguntas (0.1 pts) sin repetir las que están en
 * pantalla; se marcan las que quepan. Las sugerencias ya pagadas quedan
 * guardadas en el cargo hasta que se guarda el set o se piden otras.
 *
 * @param {Object|null} interviewQuestions - Set del cargo (position.interviewQuestions)
 * @param {Function} onChange - Recibe el set guardado para actualizar la vacante en pantalla
 */
const AtsPositionInterviewQuestionsDialog = ({
  open,
  onClose,
  positionId,
  positionName = '',
  interviewQuestions = null,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const { organization, authToken, showAlert } = useStore.getState();
  const headers = { Authorization: `Bearer ${authToken}` };

  const [draft, setDraft] = useState(() => toDraft(interviewQuestions?.questions));
  const [baseVersion, setBaseVersion] = useState(interviewQuestions?.version || 0);
  const [suggestions, setSuggestions] = useState(interviewQuestions?.suggestions || []);
  const [selected, setSelected] = useState([]); // ids de sugerencias marcadas
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false); // pedir otras sugerencias
  const [confirmLeave, setConfirmLeave] = useState(false); // atrás con cambios sin guardar
  const [conflictSet, setConflictSet] = useState(null); // 409: set actual del servidor

  const room = MAX_QUESTIONS - draft.length;
  const atMax = room <= 0;
  const hasBlank = draft.some((q) => !q.text.trim());
  const busy = suggesting || saving;
  const showEmpty = draft.length === 0 && !adding && suggestions.length === 0;

  // ── Borrador ──────────────────────────────────────────────────────────────

  const handleTextChange = (key, value) => {
    setDraft((prev) => prev.map((q) => (q.key === key ? { ...q, text: value } : q)));
    setDirty(true);
  };

  const handleRemove = (key) => {
    setDraft((prev) => prev.filter((q) => q.key !== key));
    setDirty(true);
  };

  const handleAddManual = () => {
    const text = newText.trim();
    if (!text || atMax) return;
    setDraft((prev) => [...prev, { key: newTempKey(), id: null, suggestionId: null, text, rationale: null }]);
    setNewText('');
    setAdding(false);
    setDirty(true);
  };

  // ── Sugerencias de la IA ──────────────────────────────────────────────────

  const doSuggest = async () => {
    setConfirmReplace(false);
    setSuggesting(true);
    try {
      const { data } = await api.post(
        `/positions/${positionId}/interview-questions/suggest`,
        {
          language: lang,
          organizationId: organization,
          existingQuestions: draft.map((q) => q.text.trim()).filter(Boolean),
        },
        { headers }
      );
      if (data.response?.status_response === 'OK') {
        const set = data.response.data?.interviewQuestions || null;
        setSuggestions(set?.suggestions || []);
        setSelected([]);
        // Las preguntas guardadas no cambian: solo se reflejan las sugerencias
        // pendientes en la vacante en pantalla (para verlas al volver a abrir).
        if (set && onChange) onChange(set);
        showAlert(t('ats.interviewQuestions.suggested'), 'success');
      } else {
        showAlert(data.response?.message || t('ats.interviewQuestions.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      const status = err.response?.status;
      const payload = err.response?.data?.response;
      if (status === 403) {
        // El 403 cubre dos casos distintos: el servicio no está habilitado para
        // la organización, o sí lo está pero no quedan puntos.
        showAlert(
          payload?.reason === 'SERVICE_NOT_AVAILABLE'
            ? t('ats.interviewQuestions.serviceNotAvailable')
            : t('ats.interviewQuestions.limitReached'),
          'error'
        );
      } else {
        showAlert(payload?.message || t('ats.interviewQuestions.error'), 'error');
      }
    } finally {
      setSuggesting(false);
    }
  };

  // Si hay sugerencias sin revisar, pedir otras las reemplaza (y se cobran)
  const requestSuggestions = () => {
    if (suggestions.length > 0) {
      setConfirmReplace(true);
      return;
    }
    doSuggest();
  };

  const toggleSuggestion = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= room) return prev;
      return [...prev, id];
    });
  };

  const handleAddSelected = () => {
    const chosen = suggestions.filter((s) => selected.includes(s.id)).slice(0, Math.max(room, 0));
    if (chosen.length === 0) return;
    setDraft((prev) => [
      ...prev,
      ...chosen.map((s) => ({ key: s.id, id: null, suggestionId: s.id, text: s.text, rationale: s.rationale || null })),
    ]);
    setSuggestions((prev) => prev.filter((s) => !selected.includes(s.id)));
    setSelected([]);
    setDirty(true);
  };

  const handleDiscardSuggestions = () => {
    setSuggestions([]);
    setSelected([]);
    setDirty(true);
  };

  // ── Guardar / Atrás ───────────────────────────────────────────────────────

  const handleSave = async (versionOverride = null) => {
    if (hasBlank) {
      showAlert(t('ats.interviewQuestions.blankQuestions'), 'warning');
      return;
    }
    const version = versionOverride !== null ? versionOverride : baseVersion;
    setSaving(true);
    try {
      const { data } = await api.put(
        `/positions/${positionId}/interview-questions`,
        {
          baseVersion: version,
          questions: draft.map((q) => ({
            ...(q.id ? { id: q.id } : {}),
            ...(q.suggestionId ? { suggestionId: q.suggestionId } : {}),
            text: q.text,
          })),
        },
        { headers }
      );
      const set = data.response?.data || null;
      if (set && onChange) onChange(set);
      setDirty(false);
      showAlert(t('ats.interviewQuestions.positionSaved'), 'success');
      onClose();
    } catch (err) {
      console.error('Error guardando preguntas del cargo:', err);
      const status = err.response?.status;
      const payload = err.response?.data?.response;
      if (status === 409 && payload?.errorCode === 'VERSION_CONFLICT') {
        setConflictSet(payload?.data?.interviewQuestions || { questions: [], version: 0 });
      } else if (payload?.errorCode === 'MAX_QUESTIONS_REACHED') {
        showAlert(t('ats.interviewQuestions.maxReached', { max: MAX_QUESTIONS }), 'error');
      } else if (payload?.errorCode === 'BLANK_QUESTION') {
        showAlert(t('ats.interviewQuestions.blankQuestions'), 'warning');
      } else {
        showAlert(t('ats.interviewQuestions.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // Conflicto: descartar lo propio y cargar la versión que guardó el otro usuario
  const handleConflictReload = () => {
    const set = conflictSet || {};
    setDraft(toDraft(set.questions));
    setBaseVersion(set.version || 0);
    setSuggestions(set.suggestions || []);
    setSelected([]);
    setDirty(false);
    setConflictSet(null);
    if (onChange) onChange(set);
  };

  // Conflicto: guardar lo propio igual, sobre la versión actual
  const handleConflictOverwrite = () => {
    const version = conflictSet?.version || 0;
    setBaseVersion(version);
    setConflictSet(null);
    handleSave(version);
  };

  const handleBack = () => {
    if (busy) return;
    if (dirty) {
      setConfirmLeave(true);
      return;
    }
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={handleBack} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuestionsIcon sx={{ color: '#7C3AED' }} />
              <Typography variant="h6" fontWeight={700}>
                {t('ats.interviewQuestions.positionTitle')}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleBack} disabled={busy} aria-label={t('ats.interviewQuestions.back')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {positionName && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
              {positionName}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {showEmpty ? (
            // ── Cargo sin preguntas: sugerencias de IA o escribir a mano ──
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('ats.interviewQuestions.positionEmpty')}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block', mb: 2.5, maxWidth: 560, mx: 'auto', lineHeight: 1.5 }}
              >
                {t('ats.interviewQuestions.hint')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={requestSuggestions}
                  disabled={busy}
                  startIcon={suggesting ? <CircularProgress size={16} color="inherit" /> : <AiIcon fontSize="small" />}
                  sx={purpleContained}
                >
                  {suggesting ? t('ats.interviewQuestions.suggesting') : t('ats.interviewQuestions.suggest')}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setAdding(true)}
                  disabled={busy}
                  startIcon={<ManualIcon fontSize="small" />}
                >
                  {t('ats.interviewQuestions.writeManually')}
                </Button>
              </Box>
            </Box>
          ) : (
            // ── Borrador: editar, agregar y quitar ──
            <Box>
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="caption">
                  {t('ats.interviewQuestions.positionEditHint')}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {draft.map((question, index) => {
                  const isBlank = !question.text.trim();
                  return (
                    <Box key={question.key}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                        <Box sx={numberBadgeSx}>{index + 1}</Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5, pt: '2px', flexGrow: 1 }}
                        >
                          {question.rationale
                            ? t('ats.interviewQuestions.rationale', { text: question.rationale })
                            : ''}
                        </Typography>
                        <Tooltip title={t('ats.interviewQuestions.deleteQuestion')} placement="top">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={t('ats.interviewQuestions.deleteQuestion')}
                              onClick={() => handleRemove(question.key)}
                              disabled={busy}
                              sx={{ mt: '-4px' }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        value={question.text}
                        onChange={(e) => handleTextChange(question.key, e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        error={isBlank}
                        helperText={isBlank ? t('ats.interviewQuestions.questionRequired') : ''}
                        disabled={saving}
                      />
                    </Box>
                  );
                })}

                {/* Nueva pregunta (escrita a mano) */}
                {adding && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Box sx={{ ...numberBadgeSx, backgroundColor: '#A5A0F5' }}>{draft.length + 1}</Box>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      autoFocus
                      placeholder={t('ats.interviewQuestions.newQuestionPlaceholder')}
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      inputProps={{ maxLength: 1000 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                      <Button size="small" color="inherit" onClick={() => { setAdding(false); setNewText(''); }}>
                        {t('cancel')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAddManual}
                        disabled={!newText.trim() || atMax}
                        sx={purpleContained}
                      >
                        {t('ats.interviewQuestions.add')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!adding && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => setAdding(true)}
                      disabled={atMax || busy}
                      sx={purpleOutlined}
                    >
                      {t('ats.interviewQuestions.addQuestion')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={suggesting ? <CircularProgress size={14} color="inherit" /> : <AiIcon fontSize="small" />}
                    onClick={requestSuggestions}
                    disabled={atMax || busy}
                    sx={purpleOutlined}
                  >
                    {suggesting ? t('ats.interviewQuestions.suggesting') : t('ats.interviewQuestions.suggest')}
                  </Button>
                </Box>
                <Typography variant="caption" color={atMax ? 'warning.main' : 'text.disabled'}>
                  {atMax
                    ? t('ats.interviewQuestions.maxReached', { max: MAX_QUESTIONS })
                    : t('ats.interviewQuestions.questionsCount', { current: draft.length, max: MAX_QUESTIONS })}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Sugerencias de la IA: se marcan las que se quieren agregar */}
          {suggestions.length > 0 && (
            <Paper
              elevation={0}
              sx={{ mt: 2, border: '1px solid #E9D5FF', backgroundColor: '#FAF5FF', borderRadius: 2, p: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <AiIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('ats.interviewQuestions.suggestionsTitle')}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
                {room > 0
                  ? t('ats.interviewQuestions.suggestionsHint', { room })
                  : t('ats.interviewQuestions.suggestionsNoRoom', { max: MAX_QUESTIONS })}
              </Typography>

              {suggestions.map((s) => {
                const checked = selected.includes(s.id);
                const disabled = !checked && selected.length >= room;
                return (
                  <Box key={s.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled || busy}
                      onChange={() => toggleSuggestion(s.id)}
                      inputProps={{ 'aria-label': s.text }}
                      sx={{ p: 0.5, '&.Mui-checked': { color: '#7C3AED' } }}
                    />
                    <Box sx={{ pt: '3px' }}>
                      <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.primary'}>
                        {s.text}
                      </Typography>
                      {s.rationale && (
                        <Typography variant="caption" color="text.secondary">
                          {t('ats.interviewQuestions.rationale', { text: s.rationale })}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <Button size="small" color="inherit" onClick={handleDiscardSuggestions} disabled={busy}>
                  {t('ats.interviewQuestions.discardSuggestions')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddSelected}
                  disabled={busy || selected.length === 0}
                  sx={purpleContained}
                >
                  {t('ats.interviewQuestions.addSelected', { count: selected.length })}
                </Button>
              </Box>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleBack} color="inherit" disabled={busy}>
            {t('ats.interviewQuestions.back')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave()}
            disabled={busy || !dirty}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Atrás con cambios sin guardar */}
      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ats.interviewQuestions.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('ats.interviewQuestions.unsavedConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)} color="inherit">
            {t('ats.interviewQuestions.keepEditing')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { setConfirmLeave(false); onClose(); }}
          >
            {t('ats.interviewQuestions.leaveWithoutSaving')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pedir otras sugerencias reemplaza las pendientes (y se cobran de nuevo) */}
      <Dialog open={confirmReplace} onClose={() => setConfirmReplace(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ats.interviewQuestions.replaceSuggestionsTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('ats.interviewQuestions.replaceSuggestionsConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReplace(false)} color="inherit">
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={doSuggest} sx={purpleContained}>
            {t('ats.interviewQuestions.replaceSuggestionsAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Otro usuario guardó las preguntas mientras se editaban */}
      <Dialog open={Boolean(conflictSet)} onClose={() => setConflictSet(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ats.interviewQuestions.conflictTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('ats.interviewQuestions.conflictText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConflictReload} color="inherit">
            {t('ats.interviewQuestions.conflictReload')}
          </Button>
          <Button variant="contained" onClick={handleConflictOverwrite} sx={purpleContained}>
            {t('ats.interviewQuestions.conflictOverwrite')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AtsPositionInterviewQuestionsDialog;
